import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import { Card, CardHeader, CardBody, Button, EmptyState } from '../../ui';

const SERVICES = ['pos', 'system'];

// ok: true | false | null — по полям tenant_sync_status (<service>_ok)
const stateOf = (sync, service) => {
  const v = sync?.[`${service}_ok`];
  if (v === true || v === 1) return 'ok';
  if (v === false || v === 0) return 'down';
  return 'unknown';
};

const STYLE = {
  ok: { dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400' },
  down: { dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  unknown: { dot: 'bg-zinc-400', text: 'text-zinc-500 dark:text-zinc-400' },
};

function StateLabel({ state }) {
  const { t } = useI18n();
  return (
    <span className={clsx('inline-flex items-center gap-1.5 text-xs font-semibold', STYLE[state].text)}>
      <span className={clsx('h-2 w-2 shrink-0 rounded-full', STYLE[state].dot)} aria-hidden="true" />
      {t(`servio.states.${state}`)}
    </span>
  );
}

/** Две плитки (основная и системная служба) одной компании */
export function ServioStatusTiles({ sync, showErrors = true }) {
  const { t, fmt } = useI18n();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {SERVICES.map((service) => {
        const state = stateOf(sync, service);
        const checked = sync?.[`${service}_checked_at`];
        const since = sync?.[`${service}_down_since`];
        const error = sync?.[`${service}_error`];
        return (
          <div
            key={service}
            className={clsx(
              'rounded-lg border p-4',
              state === 'down' ? 'border-red-200 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/30' : 'border-zinc-200 dark:border-zinc-800'
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t(`servio.${service}`)}</p>
                <p className="text-xs text-zinc-500">{t(`servio.${service}Hint`)}</p>
              </div>
              <StateLabel state={state} />
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
              <span>{t('servio.checked', { time: checked ? fmt.relative(checked) : t('common.never') })}</span>
              {state === 'down' && since && <span className="text-red-600 dark:text-red-400">{t('servio.since', { time: fmt.dateTime(since) })}</span>}
            </div>
            {showErrors && state === 'down' && error && <p className="mt-2 break-words font-mono text-[11px] text-red-700 dark:text-red-300">{error}</p>}
          </div>
        );
      })}
    </div>
  );
}

/** Сводка связи с Servio по всем компаниям (консоль владельца) */
export function ServioStatusTable({ companies, onChecked }) {
  const { t, fmt } = useI18n();
  const [checking, setChecking] = useState(null);
  const rows = companies.filter((c) => c.status === 'active' && c.pos_health !== 'unconfigured');

  const check = async (company) => {
    setChecking(company.id);
    try {
      await api.post(`/admin/tenants/${company.id}/servio-check`);
      onChecked?.();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setChecking(null);
    }
  };

  const down = rows.filter((c) => SERVICES.some((s) => stateOf(c.sync, s) === 'down')).length;

  return (
    <Card>
      <CardHeader
        title={t('servio.title')}
        description={down ? t('servio.problems', { count: down }) : t('servio.subtitle')}
      />
      {!rows.length ? (
        <EmptyState title={t('servio.noCompanies')} className="py-8" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 text-left text-xs text-zinc-500 dark:border-zinc-800">
                <th className="px-5 py-2.5 font-medium">{t('employees.company')}</th>
                <th className="px-3 py-2.5 font-medium">{t('servio.pos')}</th>
                <th className="px-3 py-2.5 font-medium">{t('servio.system')}</th>
                <th className="hidden px-3 py-2.5 font-medium md:table-cell">{t('servio.lastCheck')}</th>
                <th className="px-5 py-2.5"><span className="sr-only">{t('common.actions')}</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {rows.map((c) => {
                const checked = [c.sync?.pos_checked_at, c.sync?.system_checked_at].filter(Boolean).sort().pop();
                const errors = SERVICES.filter((s) => stateOf(c.sync, s) === 'down').map((s) => c.sync[`${s}_error`]).filter(Boolean);
                return (
                  <tr key={c.id} className="align-top">
                    <td className="px-5 py-3">
                      <Link to={`/owner/companies/${c.id}?tab=pos`} className="font-medium text-zinc-900 hover:underline dark:text-zinc-100">{c.name}</Link>
                      {errors.length > 0 && <p className="mt-0.5 max-w-xs truncate font-mono text-[11px] text-red-600 dark:text-red-400" title={errors.join('\n')}>{errors[0]}</p>}
                    </td>
                    <td className="px-3 py-3"><StateLabel state={stateOf(c.sync, 'pos')} /></td>
                    <td className="px-3 py-3"><StateLabel state={stateOf(c.sync, 'system')} /></td>
                    <td className="hidden px-3 py-3 text-xs text-zinc-500 md:table-cell">{checked ? fmt.relative(checked) : t('common.never')}</td>
                    <td className="px-5 py-2 text-right">
                      <Button variant="ghost" size="sm" icon={RefreshCw} loading={checking === c.id} onClick={() => check(c)}>{t('servio.checkNow')}</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Карточка для главной кабинета компании */
export function ServioStatusCard({ sync }) {
  const { t } = useI18n();
  return (
    <Card>
      <CardHeader title={t('servio.title')} description={t('servio.subtitle')} />
      <CardBody>
        <ServioStatusTiles sync={sync} showErrors={false} />
      </CardBody>
    </Card>
  );
}
